// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { ActionIcon, Button, Grid, Group, Loader, Paper, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Dosage, MedicationStatement, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { type JSX, useEffect, useState } from 'react';
import { MEDICATION_FREQUENCY_OPTIONS, MEDICATION_UNIT_OPTIONS } from '../../../constants/medical-info';
import { useRole } from '../../../hooks/useRole';
import { hasPermission } from '../../../utils/permissions';
import { withTenantScope } from '../../../utils/tenant';

interface MedicationRow {
  id?: string;
  title: string;
  dosage: string;
  unit: string;
  frequency: string;
  frequencyOther: boolean;
  resource?: MedicationStatement;
  dirty?: boolean;
}

function getDosageText(statement: MedicationStatement): { dosage: string; unit: string; frequency: string } {
  const dose = statement.dosage?.[0];
  const doseQuantity = dose?.doseAndRate?.[0]?.doseQuantity;
  const frequency = dose?.timing?.code?.coding?.[0]?.code ?? dose?.timing?.code?.text ?? '';
  return {
    dosage: doseQuantity?.value !== undefined ? String(doseQuantity.value) : '',
    unit: doseQuantity?.unit ?? doseQuantity?.code ?? '',
    frequency,
  };
}

const OTHER_FREQUENCY_VALUE = 'other';

function isPresetFrequency(value: string): boolean {
  return MEDICATION_FREQUENCY_OPTIONS.some((option) => option.value === value);
}

function buildDosage(row: MedicationRow): Dosage[] | undefined {
  const amount = Number.parseFloat(row.dosage);
  if (!row.dosage && !row.unit && !row.frequency) {
    return undefined;
  }
  const dosage: Dosage = {
    doseAndRate:
      row.dosage && !Number.isNaN(amount)
        ? [
            {
              doseQuantity: {
                value: amount,
                unit: row.unit || undefined,
                code: row.unit || undefined,
              },
            },
          ]
        : undefined,
    timing: row.frequency
      ? {
          code: {
            coding: [
              {
                system: 'http://polarissleep.com/medication-frequency',
                code: row.frequency,
                display: MEDICATION_FREQUENCY_OPTIONS.find((o) => o.value === row.frequency)?.label ?? row.frequency,
              },
            ],
          },
        }
      : undefined,
  };
  return [dosage];
}

function toRows(statements: MedicationStatement[]): MedicationRow[] {
  return statements.map((s) => {
    const dosageText = getDosageText(s);
    return {
      id: s.id,
      title: s.medicationCodeableConcept?.text ?? s.medicationCodeableConcept?.coding?.[0]?.display ?? '',
      ...dosageText,
      frequencyOther: !!dosageText.frequency && !isPresetFrequency(dosageText.frequency),
      resource: s,
    };
  });
}

export interface MedicationsSectionProps {
  patient: Patient;
  medications: MedicationStatement[];
  loading: boolean;
  onChanged: (medications: MedicationStatement[]) => void;
  onRefresh: () => void;
}

export function MedicationsSection({
  patient,
  medications,
  loading,
  onChanged,
  onRefresh,
}: MedicationsSectionProps): JSX.Element {
  const medplum = useMedplum();
  const { role, isAdmin } = useRole();
  const canEdit = hasPermission({ role, permission: 'edit-patient-profile', isAdmin });

  const [rows, setRows] = useState<MedicationRow[]>(toRows(medications));
  const [saving, setSaving] = useState<number | 'all' | null>(null);

  useEffect(() => {
    setRows(toRows(medications));
  }, [medications]);

  const updateRow = (index: number, patch: Partial<MedicationRow>): void => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch, dirty: true } : r)));
  };

  const addRow = (): void => {
    setRows((prev) => [
      ...prev,
      { title: '', dosage: '', unit: '', frequency: '', frequencyOther: false, dirty: true },
    ]);
  };

  const removeRow = async (index: number): Promise<void> => {
    const row = rows[index];
    if (row.id) {
      try {
        await medplum.deleteResource('MedicationStatement', row.id);
      } catch (err) {
        showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
        return;
      }
    }
    const next = rows.filter((_, i) => i !== index);
    setRows(next);
    onChanged(next.filter((r) => r.resource).map((r) => r.resource as MedicationStatement));
    onRefresh();
  };

  const saveRow = async (index: number): Promise<void> => {
    const row = rows[index];
    if (!row.title.trim()) {
      showNotification({ color: 'red', message: 'Medication title is required' });
      return;
    }
    if (row.frequencyOther && !row.frequency.trim()) {
      showNotification({ color: 'red', message: 'Enter a frequency for Other' });
      return;
    }
    setSaving(index);
    try {
      const base: MedicationStatement = row.resource ?? {
        resourceType: 'MedicationStatement',
        status: 'active',
        subject: createReference(patient),
      };
      const next: MedicationStatement = {
        ...base,
        status: base.status ?? 'active',
        subject: base.subject ?? createReference(patient),
        medicationCodeableConcept: {
          text: row.title.trim(),
          coding: base.medicationCodeableConcept?.coding,
        },
        dosage: buildDosage(row),
      };
      const saved = row.id
        ? await medplum.updateResource<MedicationStatement>({ ...next, id: row.id })
        : await medplum.createResource<MedicationStatement>(withTenantScope(next));
      setRows((prev) => prev.map((r, i) => (i === index ? { ...r, id: saved.id, resource: saved, dirty: false } : r)));
      onRefresh();
      showNotification({ color: 'green', message: 'Medication saved' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(null);
    }
  };

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>Medications</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={addRow} disabled={!canEdit}>
          Add Medication
        </Button>
      </Group>

      {(() => {
        if (loading) {
          return (
            <Stack align="center" py="md">
              <Loader size="sm" />
            </Stack>
          );
        }
        if (rows.length === 0) {
          return (
            <Text c="dimmed" ta="center" py="md">
              No medications on file.
            </Text>
          );
        }
        return (
          <Stack gap="sm">
            {rows.map((row, index) => (
              <Grid key={row.id ?? `new-${index}`} align="flex-end">
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <TextInput
                    label={index === 0 ? 'Medication Title' : undefined}
                    value={row.title}
                    onChange={(e) => updateRow(index, { title: e.currentTarget.value })}
                    readOnly={!canEdit}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 4, md: 2 }}>
                  <TextInput
                    label={index === 0 ? 'Dosage' : undefined}
                    value={row.dosage}
                    onChange={(e) => updateRow(index, { dosage: e.currentTarget.value })}
                    readOnly={!canEdit}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 4, md: 2 }}>
                  <Select
                    label={index === 0 ? 'Unit' : undefined}
                    data={MEDICATION_UNIT_OPTIONS}
                    value={row.unit || null}
                    onChange={(v) => updateRow(index, { unit: v ?? '' })}
                    searchable
                    clearable
                    disabled={!canEdit}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 4, md: 3 }}>
                  <Select
                    label={index === 0 ? 'Frequency' : undefined}
                    data={[...MEDICATION_FREQUENCY_OPTIONS, { value: OTHER_FREQUENCY_VALUE, label: 'Other' }]}
                    value={row.frequencyOther ? OTHER_FREQUENCY_VALUE : row.frequency || null}
                    onChange={(v) =>
                      updateRow(index, {
                        frequency: v === OTHER_FREQUENCY_VALUE ? '' : (v ?? ''),
                        frequencyOther: v === OTHER_FREQUENCY_VALUE,
                      })
                    }
                    clearable
                    disabled={!canEdit}
                  />
                  {row.frequencyOther && (
                    <TextInput
                      mt="xs"
                      placeholder="Enter frequency"
                      value={row.frequency}
                      onChange={(event) => updateRow(index, { frequency: event.currentTarget.value })}
                      error={!row.frequency.trim() ? 'Frequency is required' : undefined}
                      readOnly={!canEdit}
                    />
                  )}
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 1 }}>
                  <Group gap={4}>
                    {row.dirty && canEdit && (
                      <Button size="xs" onClick={() => saveRow(index)} loading={saving === index}>
                        Save
                      </Button>
                    )}
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      aria-label="Remove medication"
                      onClick={() => removeRow(index)}
                      disabled={!canEdit}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Grid.Col>
              </Grid>
            ))}
          </Stack>
        );
      })()}
    </Paper>
  );
}
